import React from 'react';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import SupplierPublicAttribution from './SupplierPublicAttribution';
import { formatSupplierPublicEmissions } from 'modules/FreeSupplier/supplierPublicPresentation';

const formatEmissions = formatSupplierPublicEmissions;

const formatPublishedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// The widget renders at 8-11px type inside a
// size-reporting iframe (a third party's page, often mobile) — click, not
// hover, is the only reliably-discoverable trigger there (same "mobile tap"
// reasoning used throughout the public portal).
// triggerClassName is CgCnGoodHoverText's additive-only size override (the
// `!` important prefix wins over the component's own text-sm default — see
// its own doc comment) so this cramped host doesn't inherit a trigger sized
// for the full-width portal page.
const CN_HOVER_TRIGGER_CLASSNAME = '!text-[10px] !font-normal !max-w-none';

// The embeddable CBAM Portal widget itself — a themed, compact
// rendering of the SAME normalized portal payload the full page shows (company name,
// per-product CN/product, period, direct/indirect/total, SEFA total, freshness,
// attribution ALWAYS). Rendered by the chrome-less /public/cbam-widget/:slug
// route (PublicSupplierWidgetPage.jsx); NOT rendered with a stale/offline
// variant — the page owns the single not-available state and only mounts
// this once a payload is in hand.
export default function PortalWidget({
  payload,
  theme = '#00B894',
  layout = 'card',
  showFactors = true,
}) {
  const rows = (payload?.products || []).map((product) => ({
    cnCode: product.cnCode,
    productName: product.productName,
    installationName: product.installationName,
    revisionNumber: product.revisionNumber,
    intervalLabel: product.reportingPeriod || '—',
    emissions: {
      direct: product.direct,
      indirect: product.indirect,
      total: product.total,
      unit: 'tCO₂e/t',
    },
    sefa: { total: product.sefa },
    freshnessLabel: product.revisionNumber
      ? `Revision ${product.revisionNumber}${
          product.publishedAt ? ` · published ${formatPublishedAt(product.publishedAt)}` : ''
        }`
      : `Reporting period ${product.reportingPeriod || '—'}`,
  }));
  const shown = layout === 'compact' ? rows.slice(0, 2) : rows.slice(0, 4);
  const supplier = payload?.operator || {};

  return (
    <div
      className="bg-white rounded-2xl border shadow-sm w-full max-w-md overflow-hidden mx-auto"
      style={{ borderColor: `${theme}40` }}
    >
      {/* Brand strip + identity header */}
      <div
        className="h-1 w-full"
        style={{ background: theme }}
      />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-sm font-semibold shrink-0"
          style={{ background: `${theme}14`, color: theme }}
        >
          {(supplier.name || '?')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 m-0">
            CBAM portal
          </p>
          <p className="text-sm font-bold text-slate-900 m-0 truncate leading-tight">
            {supplier.name}
          </p>
        </div>
        <span
          className="text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0"
          style={{ color: theme, background: `${theme}1f` }}
        >
          Live
        </span>
      </div>

      {/* Factors — CN code, period, direct/indirect/total + SEFA total, freshness */}
      {showFactors &&
        (rows.length === 0 ? (
          <p className="px-4 pb-3 text-[11px] text-slate-400 m-0">
            This supplier hasn't published emissions data yet.
          </p>
        ) : layout === 'card' ? (
          <div className="p-3 pt-0 grid grid-cols-2 gap-2">
            {shown.map((row) => (
              <div
                key={`${row.cnCode}-${row.productName}-${row.installationName}-${row.intervalLabel}-${row.revisionNumber}`}
                className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5"
              >
                <div className="text-[10px] font-mono text-slate-500 m-0 truncate">
                  <CgCnGoodHoverText
                    cnCode={row.cnCode}
                    useAutonomousData
                    variant="plain"
                    trigger="click"
                    triggerClassName={CN_HOVER_TRIGGER_CLASSNAME}
                  />
                </div>
                <p className="text-[9px] text-slate-400 m-0 truncate">
                  {row.intervalLabel}
                  {row.productName ? ` · ${row.productName}` : ''}
                </p>
                <p
                  className="text-base font-bold m-0 mt-0.5"
                  style={{ color: theme }}
                >
                  {formatEmissions(row.emissions.total)}
                  <span className="text-[9px] font-normal text-slate-400 ml-1">
                    {row.emissions.unit} total
                  </span>
                </p>
                <p className="text-[9px] text-slate-400 m-0 mt-0.5">
                  Direct {formatEmissions(row.emissions.direct)} · Indirect{' '}
                  {formatEmissions(row.emissions.indirect)}
                </p>
                <p className="text-[9px] text-slate-500 m-0">
                  SEFA {formatEmissions(row.sefa?.total)}
                </p>
                <p className="text-[8px] text-slate-400 m-0 mt-0.5">{row.freshnessLabel}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-3 pb-2">
            <div className="rounded-xl border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-[1fr_52px_52px_44px_52px] text-[8px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2.5 py-1.5">
                <span>Product</span>
                <span className="text-right">Direct</span>
                <span className="text-right">Indirect</span>
                <span className="text-right">SEFA</span>
                <span className="text-right">Total</span>
              </div>
              {shown.map((row) => (
                <div
                  key={`${row.cnCode}-${row.productName}-${row.installationName}-${row.intervalLabel}-${row.revisionNumber}`}
                  className="grid grid-cols-[1fr_52px_52px_44px_52px] items-center px-2.5 py-1.5 text-[10px] border-t border-slate-100"
                >
                  <div className="min-w-0">
                    <CgCnGoodHoverText
                      cnCode={row.cnCode}
                      useAutonomousData
                      variant="plain"
                      trigger="click"
                      triggerClassName={`${CN_HOVER_TRIGGER_CLASSNAME} !max-w-[70px]`}
                    />
                    <span className="text-slate-400">
                      {' '}
                      · {row.intervalLabel}
                      {row.productName ? ` · ${row.productName}` : ''}
                    </span>
                  </div>
                  <span className="text-right text-slate-600">
                    {formatEmissions(row.emissions.direct)}
                  </span>
                  <span className="text-right text-slate-600">
                    {formatEmissions(row.emissions.indirect)}
                  </span>
                  <span className="text-right text-slate-500">
                    {formatEmissions(row.sefa?.total)}
                  </span>
                  <span
                    className="text-right font-bold"
                    style={{ color: theme }}
                  >
                    {formatEmissions(row.emissions.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

      {/* Footer — attribution is ALWAYS rendered (D34, mandatory). */}
      <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-end gap-2">
        <SupplierPublicAttribution className="inline-flex items-center gap-1.5 no-underline" />
      </div>
    </div>
  );
}
